import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, ClipboardList, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingCard, fmtDate } from './shared';
import { BilanGeneralView } from './bilan/BilanGeneralView';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NouveauBilanDialog = ({ dossierId, onCreated, onClose }) => {
  const [form, setForm] = useState({ date_bilan: new Date().toISOString().slice(0, 10), type: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { const r = await axios.post(`${API_URL}/bilans/${dossierId}`, { date_bilan: form.date_bilan, type: form.type || null }); toast.success('Bilan créé'); onCreated(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="nouveau-bilan-dialog">
        <DialogHeader><DialogTitle>Nouveau bilan général</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block space-y-1"><span className="text-xs font-semibold text-slate-500">Date du bilan</span><Input type="date" value={form.date_bilan} onChange={(e) => setForm({ ...form, date_bilan: e.target.value })} data-testid="nouveau-bilan-date" /></label>
          <label className="block space-y-1"><span className="text-xs font-semibold text-slate-500">Type</span>
            <select className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="nouveau-bilan-type">
              <option value="">Automatique (intermédiaire si avant la fin de l'action, sinon final)</option><option value="intermediaire">Intermédiaire</option><option value="final">Final</option>
            </select></label>
          <p className="text-xs text-slate-400">Les indicateurs sont calculés du début de l'action jusqu'à la date du bilan. Les compétences et avis du bilan précédent sont repris.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button className="bg-indigo-600 hover:bg-indigo-700" disabled={saving || !form.date_bilan} onClick={submit} data-testid="nouveau-bilan-submit">Créer le bilan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BilansTab = ({ dossierId }) => {
  const [bilans, setBilans] = useState(null);
  const [current, setCurrent] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => axios.get(`${API_URL}/bilans/${dossierId}`).then(r => setBilans(r.data)).catch(() => toast.error('Erreur lors du chargement des bilans')), [dossierId]);
  useEffect(() => { load(); }, [load]);
  const remove = async (b) => {
    if (!window.confirm(`Supprimer le bilan du ${fmtDate(b.date_bilan)} ?`)) return;
    try { await axios.delete(`${API_URL}/bilans/${dossierId}/${b.id}`); toast.success('Bilan supprimé'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  if (current) return <BilanGeneralView dossierId={dossierId} bilanId={current} onBack={() => { setCurrent(null); load(); }} />;
  if (!bilans) return <LoadingCard />;
  return (
    <div className="space-y-4" data-testid="bilans-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-600">Bilans généraux de l'action — intermédiaires en cours de formation, final en fin d'action.</p>
        <Button type="button" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setCreating(true)} data-testid="nouveau-bilan-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau bilan</Button>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0 divide-y">
          {bilans.map(b => (
            <div key={b.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => setCurrent(b.id)} data-testid={`bilan-item-${b.id}`}>
              <div className="flex items-center gap-3 text-sm">
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${b.type === 'final' ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'}`}>{b.type === 'final' ? 'Final' : 'Intermédiaire'}</span>
                <span className="font-semibold text-slate-800">Bilan du {fmtDate(b.date_bilan)}</span>
                <span className="text-xs text-slate-400">par {b.auteur} · {b.nb_decisions} décision(s){b.observations_formateur ? ' · observations saisies' : ''}</span>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" className="p-1.5 text-slate-300 hover:text-red-500" onClick={(e) => { e.stopPropagation(); remove(b); }} title="Supprimer" data-testid={`bilan-delete-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          ))}
          {bilans.length === 0 && <p className="text-sm text-slate-400 text-center py-8" data-testid="bilans-empty">Aucun bilan pour cette action. Créez un premier bilan intermédiaire ou final.</p>}
        </CardContent>
      </Card>
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Questions de satisfaction personnalisables dans Listes → Questionnaires de satisfaction.</p>
      {creating && <NouveauBilanDialog dossierId={dossierId} onClose={() => setCreating(false)} onCreated={(b) => { setCreating(false); load(); setCurrent(b.id); }} />}
    </div>
  );
};
